import { customSkillDefinition } from '../definitions/customSkill';
import { geographyDefinition } from '../definitions/geography';
import {
  type Filter,
  FilterId,
  FilterType,
  isMultiFilter,
  type TreeFilter,
} from '../types/filters';

export const TREEMAP_DRILLDOWN_SEGMENTS = [
  'geography',
  'role',
  'rics',
  'skills',
] as const;

export type TreemapDrilldownSegment =
  (typeof TREEMAP_DRILLDOWN_SEGMENTS)[number];

export const ROLE_DRILLDOWN_LEVEL_ORDER = [
  'role_k7',
  'role_k50',
  'role_k150',
  'role_k500',
  'role_k1500',
  'role_k5000',
  'role_k15000',
] as const;

const ROLE_LEAF_LEVELS = new Set<string>([
  'role_k50',
  'role_k500',
  'role_k5000',
  'role_k15000',
]);

const LEVEL_ORDER_BY_SEGMENT: Record<
  TreemapDrilldownSegment,
  readonly string[]
> = {
  geography: geographyDefinition.selectionListIds,
  role: ROLE_DRILLDOWN_LEVEL_ORDER,
  rics: ['rics_k10', 'rics_k50', 'rics_k400', 'company'],
  skills: customSkillDefinition.selectionListIds,
};

export function getTreemapDrilldownLevelOrder(
  segment: TreemapDrilldownSegment
): readonly string[] {
  return LEVEL_ORDER_BY_SEGMENT[segment];
}

export function getTreemapDrilldownIsLeafLevel(
  segment: TreemapDrilldownSegment,
  levelId: string
): boolean {
  if (segment === 'role') {
    return ROLE_LEAF_LEVELS.has(levelId);
  }
  const levelOrder = LEVEL_ORDER_BY_SEGMENT[segment];
  const lastLevel = levelOrder[levelOrder.length - 1];
  return lastLevel !== undefined && levelId === lastLevel;
}

export function isTreemapDrilldownSegment(
  dim: string | undefined
): dim is TreemapDrilldownSegment {
  return TREEMAP_DRILLDOWN_SEGMENTS.includes(dim as TreemapDrilldownSegment);
}

const FILTER_ID_BY_SEGMENT: Record<TreemapDrilldownSegment, FilterId> = {
  geography: FilterId.GEOGRAPHY,
  role: FilterId.ROLE,
  rics: FilterId.COMPANY,
  skills: FilterId.CUSTOM_SKILL,
};

const MULTI_FILTER_ID_BY_SEGMENT: Partial<
  Record<TreemapDrilldownSegment, FilterId>
> = {
  geography: FilterId.MULTI_GEOGRAPHY,
  role: FilterId.MULTI_ROLE,
  rics: FilterId.MULTI_COMPANY,
  skills: FilterId.MULTI_CUSTOM_SKILL,
};

function getTreeSelectionsFromFilter(
  filter: Filter,
  segment: TreemapDrilldownSegment
): TreeFilter['selections'] | null {
  const targetId = FILTER_ID_BY_SEGMENT[segment];
  const multiId = MULTI_FILTER_ID_BY_SEGMENT[segment];
  if (filter.id === targetId && filter.type === FilterType.TREE) {
    return (filter as TreeFilter).selections;
  }
  if (
    multiId !== undefined &&
    filter.id === multiId &&
    isMultiFilter(filter) &&
    filter.instances.length > 0
  ) {
    const firstInstance = filter.instances[0];
    if (firstInstance !== undefined && firstInstance.type === FilterType.TREE) {
      return (firstInstance as TreeFilter).selections;
    }
  }
  return null;
}

export function getTreemapInitialLevelOffset(
  segment: TreemapDrilldownSegment,
  effectiveFilters: Filter[]
): number {
  const targetId = FILTER_ID_BY_SEGMENT[segment];
  const multiId = MULTI_FILTER_ID_BY_SEGMENT[segment];
  const filter = effectiveFilters.find(
    (f) => f.id === targetId || f.id === multiId
  );
  if (filter === undefined) return 0;

  const selections = getTreeSelectionsFromFilter(filter, segment);
  if (selections === null || selections.length === 0) return 0;

  const levelOrder = LEVEL_ORDER_BY_SEGMENT[segment];
  const deepestIndex = selections.reduce((max, sel) => {
    const idx = levelOrder.indexOf(sel.levelId);
    return idx === -1 ? max : Math.max(max, idx);
  }, -1);
  if (deepestIndex === -1) return 0;
  return deepestIndex + 1;
}
